import { useState, useCallback, useEffect } from 'react';
import { useDatabase } from '../db';
import type { OrderDocType } from '@lancar-pos/db-schema';

export type Order = OrderDocType;
export type OrderItem = OrderDocType['items'][number];

function generateOrderNumber(): string {
  const date = new Date();
  const dateStr = `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}`;
  const random = String(Date.now()).slice(-4);
  return `ORD-${dateStr}-${random}`;
}

export function useOrderStore() {
  const db = useDatabase();
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsLoading(true);
    const subscription = db.orders
      .find({
        selector: { _deleted: false },
        sort: [{ createdAt: 'desc' }],
      })
      .$.subscribe((docs: Array<{ toMutableJSON: () => Order }>) => {
        setOrders(docs.map((doc: { toMutableJSON: () => Order }) => doc.toMutableJSON() as Order));
        setIsLoading(false);
      });

    return () => subscription.unsubscribe();
  }, [db.orders]);

  const addOrder = useCallback(
    async (order: Omit<Order, 'id' | 'orderNumber' | 'createdAt' | 'updatedAt' | '_deleted'>): Promise<Order> => {
      const now = new Date().toISOString();
      const orderNumber = generateOrderNumber();
      const id = crypto.randomUUID();
      const newOrder: Order = {
        ...order,
        id,
        orderNumber,
        createdAt: now,
        updatedAt: now,
        _deleted: false,
      };
      await db.orders.insert(newOrder);
      return newOrder;
    },
    [db.orders]
  );

  const updateOrder = useCallback(
    async (id: string, updates: Partial<Omit<Order, 'id' | 'createdAt'>>) => {
      const doc = await db.orders.findOne(id).exec();
      if (doc) {
        await doc.patch({
          ...updates,
          updatedAt: new Date().toISOString(),
        });
      }
    },
    [db.orders]
  );

  const cancelOrder = useCallback(
    async (id: string) => {
      const doc = await db.orders.findOne(id).exec();
      if (doc) {
        await doc.patch({
          status: 'cancelled',
          updatedAt: new Date().toISOString(),
        });
      }
    },
    [db.orders]
  );

  const getOrderById = useCallback(
    (id: string): Order | undefined => {
      return orders.find((o) => o.id === id);
    },
    [orders]
  );

  const getOrdersByStatus = useCallback(
    (status: Order['status']): Order[] => {
      return orders.filter((o) => o.status === status);
    },
    [orders]
  );

  const getOrdersByDateRange = useCallback(
    (startDate: Date, endDate: Date): Order[] => {
      return orders.filter((o) => {
        const createdAt = new Date(o.createdAt);
        return createdAt >= startDate && createdAt <= endDate;
      });
    },
    [orders]
  );

  const getTodayOrders = useCallback((): Order[] => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return getOrdersByDateRange(today, tomorrow);
  }, [getOrdersByDateRange]);

  return {
    orders,
    isLoading,
    addOrder,
    updateOrder,
    cancelOrder,
    getOrderById,
    getOrdersByStatus,
    getOrdersByDateRange,
    getTodayOrders,
  };
}

export type OrderStore = ReturnType<typeof useOrderStore>;
